// @aiContributed-MAPFuse-2026-02-28
import _ from 'underscore';
import { useContext, useMemo, useCallback, useState, useEffect } from 'react';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import type { Flight, ServerContextType, UseFlightsReturn, UseFlightsParams, FlightStatus } from './index.d';
import {
  FLIGHT_STATUS,
} from './constants';
import { getCampaignFlightAllocations } from './fetch-flights';
import { isFlightMatch, getFlightUrlOverrideStatus } from './utils';
import { useFlightInstrumentation } from './use-flight-instrumentation';

export type UseCampaignFlightsParams = UseFlightsParams & {
  campaignId: number;
};

const useCampaignFlights = ({
  treatmentFlightId,
  controlFlightId,
  treatmentFlightName,
  controlFlightName,
  campaignId,
  autoLog = true,
}: UseCampaignFlightsParams): UseFlightsReturn => {
  const [shouldLog, setShouldLog] = useState(autoLog);
  const serverContext = useContext<ServerContextType>(ServerContext);
  const { Id: aid = 0, AdvertiserCustomerID: cid = 0 } = serverContext.CurrentAccount || {};
  const oDataToken = serverContext.Token || '';

  const [experimentFlight, setExperimentFlight] = useState<Flight | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);

  const { flightActivity, nonFlightActivity, allFlightActivity } = useFlightInstrumentation('CampaignFlights');

  const campaignApiBase = serverContext?.ServicesEndpoints?.CampaignAPI;

  useEffect(() => {
    if (!campaignId || !aid || !cid) {
      allFlightActivity.trace(JSON.stringify({
        campaignId, aid, cid, reason: 'Missing campaignId, aid, or cid',
      }));
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    getCampaignFlightAllocations({
      campaignId, aid, cid, campaignApiBase, oDataToken, activity: allFlightActivity,
    })
      .then((flights: Flight[]) => {
        const flight = _.find(flights, (f: Flight): boolean =>
          isFlightMatch(f, treatmentFlightId, treatmentFlightName) ||
          isFlightMatch(f, controlFlightId, controlFlightName));
        setExperimentFlight(flight);
        setIsLoading(false);
      })
      .catch((error: { message?: string }) => {
        allFlightActivity.trace(JSON.stringify({ campaignId, error: error?.message }));
        setIsLoading(false);
      });
  }, [campaignId, aid, cid, campaignApiBase, oDataToken, allFlightActivity, controlFlightId, treatmentFlightId, treatmentFlightName, controlFlightName]);

  const log = useCallback(() => {
    if (experimentFlight) {
      flightActivity.trace(JSON.stringify(experimentFlight));
    } else {
      nonFlightActivity.trace(JSON.stringify({ treatmentFlightId, controlFlightId, campaignId }));
    }
  }, [flightActivity, nonFlightActivity, treatmentFlightId, controlFlightId, campaignId, experimentFlight]);

  const urlOverride = useMemo(
    () => getFlightUrlOverrideStatus(treatmentFlightId, treatmentFlightName),
    [treatmentFlightId, treatmentFlightName]
  );

  const flightStatus = useMemo((): FlightStatus => {
    if (isLoading) {
      return FLIGHT_STATUS.LOADING;
    }

    // support flight url override (e.g. ?flights=123,myFlightName)
    if (urlOverride !== null) {
      return urlOverride;
    }

    if (isFlightMatch(experimentFlight, treatmentFlightId, treatmentFlightName)) {
      return FLIGHT_STATUS.IN_TREATMENT;
    } else if (isFlightMatch(experimentFlight, controlFlightId, controlFlightName)) {
      return FLIGHT_STATUS.IN_CONTROL;
    }

    return FLIGHT_STATUS.NOT_IN_FLIGHT;
  }, [isLoading, experimentFlight, urlOverride, treatmentFlightId, controlFlightId, treatmentFlightName, controlFlightName]);

  const logFlight = useCallback(() => {
    setShouldLog(true);
  }, []);

  useEffect(() => {
    if (shouldLog && urlOverride === null && flightStatus !== FLIGHT_STATUS.LOADING) {
      log();
    }
  }, [shouldLog, urlOverride, flightStatus, log]);

  return {
    flightStatus,
    cohortIndex: experimentFlight?.CohortIndex ?? undefined,
    rampAllocationId: experimentFlight?.RampAllocationId ?? undefined,
    ...(autoLog ? {} : { logFlight }),
  };
};

export { useCampaignFlights };
